/**
 * Callout Component
 *
 * Displays highlighted information boxes for tips, warnings, etc.
 * Used in guidance content.
 */

import { clsx } from 'clsx';
import {
  Lightbulb,
  AlertTriangle,
  Info,
  CheckCircle,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { PortableText, type PortableTextReactComponents } from '@portabletext/react';
import type { SanityBlock } from '@/lib/sanity';

type CalloutType = 'tip' | 'warning' | 'info' | 'success' | 'example';

interface CalloutProps {
  type: CalloutType;
  title?: string;
  content: SanityBlock[];
  className?: string;
}

// Callout type configurations
const calloutConfig: Record<
  CalloutType,
  {
    icon: LucideIcon;
    label: string;
    containerClass: string;
    iconClass: string;
    titleClass: string;
  }
> = {
  tip: {
    icon: Lightbulb,
    label: 'Tip',
    containerClass: 'bg-amber-50 border-amber-200',
    iconClass: 'text-amber-600',
    titleClass: 'text-amber-800',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Warning',
    containerClass: 'bg-red-50 border-red-200',
    iconClass: 'text-red-600',
    titleClass: 'text-red-800',
  },
  info: {
    icon: Info,
    label: 'Note',
    containerClass: 'bg-blue-50 border-blue-200',
    iconClass: 'text-blue-600',
    titleClass: 'text-blue-800',
  },
  success: {
    icon: CheckCircle,
    label: 'Success',
    containerClass: 'bg-green-50 border-green-200',
    iconClass: 'text-green-600',
    titleClass: 'text-green-800',
  },
  example: {
    icon: Target,
    label: 'Example',
    containerClass: 'bg-purple-50 border-purple-200',
    iconClass: 'text-purple-600',
    titleClass: 'text-purple-800',
  },
};

// Simplified components for callout content
const calloutComponents: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => (
      <p className="text-stone-700 text-sm leading-relaxed">{children}</p>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside text-stone-700 text-sm space-y-1 mt-2">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside text-stone-700 text-sm space-y-1 mt-2">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic">{children}</em>
    ),
  },
};

export function Callout({ type, title, content, className }: CalloutProps) {
  const config = calloutConfig[type];
  const Icon = config.icon;
  const displayTitle = title || config.label;

  return (
    <div
      className={clsx(
        'rounded-lg border p-4 my-4',
        config.containerClass,
        className
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={clsx('w-5 h-5 mt-0.5 flex-shrink-0', config.iconClass)} />
        <div className="flex-1 min-w-0">
          <h4 className={clsx('font-semibold text-sm mb-1', config.titleClass)}>
            {displayTitle}
          </h4>
          <div className="text-stone-600">
            <PortableText value={content} components={calloutComponents} />
          </div>
        </div>
      </div>
    </div>
  );
}
